import { MenuItem } from '@waves/menu';
import { Popover } from '@waves/popover';
import React, {
  ReactElement,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';

import { KitMenuProps, RefType } from './types';

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (!window.customElements.get('waves-popover')) {
    window.customElements.define('waves-popover', Popover);
  }

  if (!window.customElements.get('waves-menu-item')) {
    window.customElements.define('waves-menu-item', MenuItem);
  }
}

const KitMenu = forwardRef<RefType, KitMenuProps>(
  (props, ref): ReactElement => {
    const {
      menuContainerStyle,
      menuItemsContainerStyle,
      show,
      onOpen,
      onClose,
      children,
    } = props;
    const popoverRef = useRef<RefType>(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [currentContent, setCurrentContent] = useState<ReactNode>(children);

    useEffect(() => {
      if (show) {
        setCurrentContent(children);
        setIsTransitioning(false);
        popoverRef.current?.open();
      } else {
        // Freeze content state before transition starts
        setCurrentContent((prev) => prev || children);
        setIsTransitioning(true);
        popoverRef.current?.close();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
      if (!isTransitioning && show) {
        setCurrentContent(children);
      }
    }, [children, show, isTransitioning]);

    const handleOpenAnimation = useCallback(() => {
      onOpen?.();
    }, [onOpen]);

    const handleCloseAnimation = useCallback(() => {
      if (!show) {
        setIsTransitioning(false);
        setCurrentContent(null);
        onClose?.();
      }
    }, [show, onClose]);

    useEffect(() => {
      document.addEventListener('open', handleOpenAnimation);
      document.addEventListener('close', handleCloseAnimation);

      return () => {
        document.removeEventListener('open', handleOpenAnimation);
        document.removeEventListener('close', handleCloseAnimation);
      };
    }, [handleOpenAnimation, handleCloseAnimation]);

    const handleMenuClick = useCallback((event: MouseEvent) => {
      event.stopPropagation();
    }, []);

    // Always use currentContent during transitions to prevent flicker
    const displayMenuContent = isTransitioning ? currentContent : children;

    return (
      <View style={menuContainerStyle} testID='kit_menu_web'>
        <waves-popover
          ref={popoverRef}
          placement='top-end'
          disable-repositioning
          style={
            { '--waves-popover-padding': '0', zIndex: 2 } as React.CSSProperties
          }
        >
          <div
            role='menu'
            style={{
              ...menuItemsContainerStyle,
            }}
            onClick={handleMenuClick as any}
          >
            {displayMenuContent}
          </div>
        </waves-popover>
      </View>
    );
  }
);

export default KitMenu;
